import { useCallback, useState } from 'react';

import { useLocalSignerShareSlideUp } from './useLocalSignerShareSlideUp';

type Params = Readonly<{
	handleOpenShareSlideUp: (options?: Readonly<{ skipQrCodeExperiment?: boolean }>) => void;
	handleCloseShareSlideUp: () => void;
}>;

type Result = Readonly<{
	isOpen: boolean;
	open: () => void;
	onOpenChange: (open: boolean) => void;
	/** EXPERIMENT: constituent_psf_experiment. Whether (and with what) the sheet opens on the `v2` share screen. */
	localSignerShare: ReturnType<typeof useLocalSignerShareSlideUp>;
}>;

/**
 * The share slide-up's open state and its open/close handlers. Every dismissal (close button, ESC,
 * click-away) reaches `onOpenChange(false)`, which tracks the close and resets the open state.
 *
 * EXPERIMENT: constituent_psf_experiment. Opening also enrolls a local signer (every arm), and a `v2`
 * local signer's sheet skips the QR code experiment, since it shows the new share screen, not the grid.
 */
export function useSlideUpOpenState({ handleOpenShareSlideUp, handleCloseShareSlideUp }: Params): Result {
	const [isOpen, setIsOpen] = useState(false);
	const localSignerShare = useLocalSignerShareSlideUp();
	const { showsLocalSignerShare, enrollOnOpen: enrollConstituentOnOpen } = localSignerShare;

	const open = useCallback(() => {
		handleOpenShareSlideUp({ skipQrCodeExperiment: showsLocalSignerShare });
		enrollConstituentOnOpen();
		setIsOpen(true);
	}, [handleOpenShareSlideUp, showsLocalSignerShare, enrollConstituentOnOpen]);

	const onOpenChange = useCallback(
		(nextOpen: boolean) => {
			if (nextOpen) return;
			handleCloseShareSlideUp();
			setIsOpen(false);
		},
		[handleCloseShareSlideUp],
	);

	return { isOpen, open, onOpenChange, localSignerShare };
}
